import * as tf from '@tensorflow/tfjs';
import * as faceapi from 'face-api.js';

const MODEL_URL = '/models';
const LIVE_FACE_INPUT_SIZE = 224;
const LIVE_FACE_SCORE_THRESHOLD = 0.5;

export const LIVE_FACE_DETECTION_INTERVAL_MS = 350;

let backendPromise = null;
let liveModelsPromise = null;
let recognitionModelsPromise = null;

async function ensureFaceBackend() {
  if (!backendPromise) {
    backendPromise = (async () => {
      try {
        if (tf.getBackend() !== 'webgl') {
          const hasWebgl = await tf.setBackend('webgl');
          if (!hasWebgl) {
            await tf.setBackend('cpu');
          }
        }

        await tf.ready();

        return tf.getBackend();
      } catch (error) {
        console.warn('Failed to initialize WebGL backend for face-api, falling back to CPU.', error);
        await tf.setBackend('cpu');
        await tf.ready();

        return tf.getBackend();
      }
    })().catch((error) => {
      backendPromise = null;
      throw error;
    });
  }

  return backendPromise;
}

export async function loadLiveFaceModels() {
  if (!liveModelsPromise) {
    liveModelsPromise = (async () => {
      await ensureFaceBackend();

      const loaders = [];

      if (!faceapi.nets.tinyFaceDetector.isLoaded) {
        loaders.push(faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL));
      }

      if (!faceapi.nets.faceLandmark68TinyNet.isLoaded) {
        loaders.push(faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL));
      }

      await Promise.all(loaders);
      return true;
    })().catch((error) => {
      liveModelsPromise = null;
      throw error;
    });
  }

  return liveModelsPromise;
}

export async function loadRecognitionFaceModels() {
  if (!recognitionModelsPromise) {
    recognitionModelsPromise = (async () => {
      await ensureFaceBackend();

      const loaders = [];

      if (!faceapi.nets.ssdMobilenetv1.isLoaded) {
        loaders.push(faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL));
      }

      if (!faceapi.nets.faceLandmark68Net.isLoaded) {
        loaders.push(faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL));
      }

      if (!faceapi.nets.faceRecognitionNet.isLoaded) {
        loaders.push(faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL));
      }

      await Promise.all(loaders);
      return true;
    })().catch((error) => {
      recognitionModelsPromise = null;
      throw error;
    });
  }

  return recognitionModelsPromise;
}

export function createLiveFaceDetectorOptions(overrides = {}) {
  return new faceapi.TinyFaceDetectorOptions({
    inputSize: LIVE_FACE_INPUT_SIZE,
    scoreThreshold: LIVE_FACE_SCORE_THRESHOLD,
    ...overrides,
  });
}

export function createRecognitionFaceDetectorOptions(overrides = {}) {
  return new faceapi.SsdMobilenetv1Options({
    minConfidence: 0.5,
    maxResults: 1,
    ...overrides,
  });
}

export function buildOptimizedCameraConstraints() {
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  if (isMobile) {
    return {
      video: {
        facingMode: 'user',
        width: { ideal: 240 },
        height: { ideal: 320 },
        frameRate: { ideal: 10, max: 15 },
      },
    };
  }

  return {
    video: {
      width: { ideal: 320 },
      height: { ideal: 240 },
      frameRate: { ideal: 12, max: 15 },
    },
  };
}
